import React, { useState, useRef, useEffect, useCallback } from 'react';
import './SentimentAnalysis.css';
import ScrollIndicator from '../ScrollIndicator';
import TearFaceIcon from '../TearFaceIcon';
import { useAnalytics } from '../../utils/analytics/useAnalytics';
import { analyzeSentiment } from '../../utils/sentimentAnalysis';

const sections = [
  { id: 'sentiment-hero-section', label: 'Welcome' },
  { id: 'sentiment-methodology-section', label: 'Research Methodology' },
  { id: 'sentiment-architecture-section', label: 'Technical Architecture' },
  { id: 'sentiment-analysis-section', label: 'Try Analysis' },
  { id: 'sentiment-use-cases-section', label: 'Use Cases' }
];

const SentimentAnalysis = () => {
  const { track } = useAnalytics();
  const [text, setText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [sentiment, setSentiment] = useState('neutral');
  const [modelReady, setModelReady] = useState(false);
  const [modelError, setModelError] = useState(false);
  const [modelProgress, setModelProgress] = useState(0);
  const textareaRef = useRef(null);
  const buttonRef = useRef(null);


useEffect(() => {
  // Listen for model loading progress and ready/error events
  let cleanupSentiment = null;
  let isUnmounted = false;

  const handleModelProgress = (event) => {
    const { status, progress } = event.detail;
    
    // Once we're ready or at 100%, never go backwards
    if (modelReady || modelProgress === 100) {
      return;
    }
    
    if (progress !== undefined && !isNaN(progress)) {
      // Ensure progress never goes backwards and caps at 99% until ready
      setModelProgress(prev => {
        // If we're already at 100% or ready, don't change
        if (prev === 100 || modelReady) return prev;
        // Don't allow progress to go backwards
        if (progress < prev) return prev;
        // Cap at 99% until we get the ready signal
        return Math.min(progress, 99);
      });
    }
  };

  // Listen for worker messages directly for 'ready' and 'error'
  const handleWorkerMessage = (event) => {
    if (!event || !event.data) return;
    if (event.data.status === 'ready') {
      // Set progress to 100 first, then set ready state after a short delay
      setModelProgress(100);
      // Small delay to ensure smooth transition
      setTimeout(() => {
        setModelReady(true);
      }, 100);
    } else if (event.data.status === 'error') {
      setModelError(true);
    }
  };

  window.addEventListener('modelLoadingProgress', handleModelProgress);

  // Patch: also listen to the worker directly for 'ready' event
  let workerRef = null;
  const initModel = async () => {
    try {
      const { initializeModel, isModelInitialized, cleanup } = await import('../../utils/sentimentAnalysis');
      cleanupSentiment = cleanup;

      // If already initialized, set ready
      if (isModelInitialized()) {
        setModelReady(true);
        setModelProgress(100);
        return;
      }

      // Hack: get the worker instance to listen for 'ready' directly
      // This is safe because the worker is created in initializeModel
      await initializeModel();
      // After initializeModel resolves, model is ready
      setModelReady(true);
      setModelProgress(100);
    } catch (error) {
      console.error('Model initialization error:', error);
      setModelError(true);
    }
  };

  initModel();

  // Listen for 'ready' and 'error' events from the worker
  if (window && window.addEventListener) {
    window.addEventListener('message', handleWorkerMessage);
  }

  return () => {
    window.removeEventListener('modelLoadingProgress', handleModelProgress);
    if (window && window.removeEventListener) {
      window.removeEventListener('message', handleWorkerMessage);
    }
    // Cleanup the worker when component unmounts
    if (cleanupSentiment) {
      cleanupSentiment();
    } else {
      // fallback
      const { cleanup } = require('../../utils/sentimentAnalysis');
      cleanup();
    }
    isUnmounted = true;
  };
}, []); // Only run once on mount

  const createRipple = (element, color) => {
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const ripple = document.createElement('div');
    ripple.className = 'ripple';
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = rect.left + rect.width / 2 - size / 2 + 'px';
    ripple.style.top = rect.top + rect.height / 2 - size / 2 + 'px';
    ripple.style.background = color;
    document.body.appendChild(ripple);
    setTimeout(() => { ripple.remove(); }, 2000);
  };

  const handleAnalyzeSentiment = useCallback(async () => {
    if (isAnalyzing || !modelReady) return;
    const trimmedText = text.trim();
    if (trimmedText === '') {
      alert('Please enter some text to analyze!');
      return;
    }
    setIsAnalyzing(true);
    try {
      const aiResult = await analyzeSentiment(trimmedText);
      let rippleColor;
      if (aiResult.sentiment === 'positive') {
        rippleColor = 'rgba(46, 204, 113, 0.6)';
      } else if (aiResult.sentiment === 'negative') {
        rippleColor = 'rgba(231, 76, 60, 0.6)';
      } else {
        rippleColor = 'rgba(52, 152, 219, 0.6)';
      }
      createRipple(textareaRef.current, rippleColor);
      
      // Track the analysis result
      track('sentiment_analysis', {
        textLength: trimmedText.length,
        sentiment: aiResult.sentiment,
        confidence: aiResult.confidence,
        status: 'success'
      });

      setTimeout(() => {
        setSentiment(aiResult.sentiment);
        setResult(aiResult);
      }, 400);
    } catch (error) {
      // Track the error
      track('sentiment_analysis_error', {
        error: error.message,
        status: 'error'
      });
      
      setModelError(true);
      setResult(null);
    } finally {
      setIsAnalyzing(false);
    }
  }, [text, isAnalyzing, modelReady, track]); // Add track to dependency array

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey && modelReady && !isAnalyzing) {
      handleAnalyzeSentiment(); // correct function
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    setResult(null);
    if (newText.trim() === '') {
      setTimeout(() => {
        setSentiment('neutral');
      }, 300);
    }
  };

  const isInputDisabled = !modelReady || isAnalyzing;

  return (
    <>
      <ScrollIndicator sections={sections} />
      <TearFaceIcon />
      <div className='sentiment-page'>
        {/* Hero Section */}
        <div className='sentiment-section sentiment-hero-section' id='sentiment-hero-section'>
          <div className='sentiment-hero-content'>
            <div className='sentiment-hero-text'>
              <h1>
                Sentiment Analysis<span className='comma'>,</span><br/>
                AI-Powered Insights<span className='dot'>.</span>
              </h1>
              <div className='research-abstract'>
                <p>
                  We experimented with porting machine learning models from Python to JavaScript environments and learned how to implement BERT-based architectures optimized for client-side inference.
                </p>
                <div className='research-keywords'>
                  <span>Keywords:</span> Sentiment Analysis, BERT, WebAssembly, Client-Side AI, Natural Language Processing
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Research Methodology Section */}
        <div className='sentiment-section sentiment-methodology-section' id='sentiment-methodology-section'>
          <h2>Research Methodology</h2>
          <div className='methodology-grid'>
            <div className='methodology-card'>
              <h3>Phase 1: Initial Approach</h3>
              <p><strong>Universal Sentence Encoder (USE)</strong> + Logistic Regression, trained in Python with attempted JavaScript port. Encountered accuracy degradation due to numerical precision differences.</p>
              <div className='tech-details'>
                <strong>Model Size:</strong> 3MB<br/>
                <strong>Accuracy:</strong> 82%<br/>
                <strong>Issue:</strong> Inconsistent 512-dimensional parameter behavior
              </div>
            </div>
            <div className='methodology-card'>
              <h3>Phase 2: Current Solution</h3>
              <p><strong>DistilBERT</strong> using ONNX runtime with WebAssembly, achieving high accuracy while maintaining full browser compatibility.</p>
              <div className='tech-details'>
                <strong>Model Size:</strong> 15MB<br/>
                <strong>Accuracy:</strong> 95%<br/>
                <strong>Runtime:</strong> ONNX + WebAssembly
              </div>
            </div>
          </div>
        </div>

        {/* Technical Architecture Section */}
        <div className='sentiment-section sentiment-architecture-section' id='sentiment-architecture-section'>
          <h2>Technical Architecture</h2>
          <div className='implementation-details'>
            <div className='tech-specs'>
              <ul>
                <li><strong>Web Worker:</strong> Isolated AI processing to prevent UI blocking</li>
                <li><strong>Model Loading:</strong> Progressive download with real-time progress tracking</li>
                <li><strong>Inference:</strong> Client-side processing with no backend dependency</li>
                <li><strong>Memory Management:</strong> Optimized for browser memory constraints</li>
              </ul>
              <div className='performance-metrics'>
                <div className='metric-grid'>
                  <div className='metric'>
                    <span className='metric-value'>50ms</span>
                    <span className='metric-label'>Inference Time</span>
                  </div>
                  <div className='metric'>
                    <span className='metric-value'>15MB</span>
                    <span className='metric-label'>Model Size</span>
                  </div>
                  <div className='metric'>
                    <span className='metric-value'>95%</span>
                    <span className='metric-label'>Accuracy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Section */}
        <div className='sentiment-section sentiment-analysis-section' id='sentiment-analysis-section'>
          <h2>Try the AI-Based Sentiment Analysis</h2>
          <div className='sentiment-analysis-container'>
            <div className='sentiment-input-container'>
              <textarea 
                ref={textareaRef}
                value={text}
                onChange={handleTextChange}
                onKeyPress={handleKeyPress}
                placeholder={modelReady ? "Type your feedback here and click analyze..." : "Loading AI model..."}
                maxLength="500"
                className={`sentiment-textarea ${sentiment}`}
                disabled={isInputDisabled}
              />
              <button 
                ref={buttonRef}
                className={`sentiment-analyze-btn ${!modelReady ? 'loading-model' : ''} ${isAnalyzing ? 'loading' : ''}`}
                onClick={handleAnalyzeSentiment}
                disabled={isInputDisabled}
              >
                {!modelReady ? `Loading AI... ${modelProgress}%` : (isAnalyzing ? 'Analyzing...' : 'Analyze')}
                {!modelReady && <span className="progress-bar indeterminate" style={{ width: `${modelProgress}%` }}></span>}
                {isAnalyzing && modelReady && <span className="progress-bar"></span>}
              </button>
              {isAnalyzing && (
                <div className="sentiment-result show shimmer">
                  <div className="sentiment-text shimmer-bar" style={{width: '60%', height: 32, margin: '0 auto', borderRadius: 8}}></div>
                  <div className="sentiment-confidence shimmer-bar" style={{width: '40%', height: 18, margin: '16px auto 0', borderRadius: 8}}></div>
                </div>
              )}
              {!isAnalyzing && result && (
                <div className="sentiment-result show">
                  <div className="sentiment-text">
                    {result.sentiment === 'positive' ? '😊' : result.sentiment === 'negative' ? '😔' : '😐'} 
                    {result.sentiment.charAt(0).toUpperCase() + result.sentiment.slice(1)}
                  </div>
                  <div className="sentiment-confidence">
                    Confidence: {result.confidence}%
                  </div>
                  {modelError && (
                    <div style={{color: '#e74c3c', fontSize: '0.9em', marginTop: 8}}>
                      AI model failed to load.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Use Cases Section */}
        <div className='sentiment-section sentiment-use-cases-section' id='sentiment-use-cases-section'>
          <h2>Use Cases</h2>
          <div className='sentiment-use-cases-grid'>
            <div className='sentiment-use-case-card'>
              <h3>Customer Feedback</h3>
              <p>Automatically analyze customer reviews and feedback to identify satisfaction levels and areas for improvement.</p>
            </div>
            <div className='sentiment-use-case-card'>
              <h3>Social Media Monitoring</h3>
              <p>Track brand sentiment across social media platforms to understand public perception in real-time.</p>
            </div>
            <div className='sentiment-use-case-card'>
              <h3>Product Development</h3>
              <p>Use sentiment analysis to prioritize feature requests and identify pain points in your products.</p>
            </div>
            <div className='sentiment-use-case-card'>
              <h3>Marketing Campaigns</h3>
              <p>Measure the emotional impact of your marketing campaigns and adjust strategies accordingly.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SentimentAnalysis;
